import React, { useState, useEffect, useCallback } from 'react';
import { ErrorMessage } from '@hookform/error-message';
import ReactButton from 'components/Buttons/ReactButton';
import DatePicker from 'components/Inputs/DatePicker.js';
import TelephoneMask from 'components/Inputs/TelephoneMask';
import { Controller, useForm } from 'react-hook-form';
import {
  Modal,
  ModalBody,
  Col,
  Form,
  Input,
  Row,
  InputGroup
} from 'reactstrap';
import { removeMaskSpecialCharacters } from 'views/CRM/shared/utils/phoneNumberHelpers';
import FlexSelect from 'components/Inputs/FlexSelect';
import ProtocolSchedulingService from '../../../service/ProtocolSchedulingService';
import { toast } from 'react-toastify';
import { maskPhoneNumber } from 'views/CRM/shared/utils/phoneNumberHelpers';
import { typeOptions } from '../../utils/schedulingOptions';
import { LabelInput } from 'views/ServicePanel/components/AttendanceContent/components/Modal/CloseConversation/atonsComponents/LabelInput';
import { DatesHelper } from 'utils/classes/DatesHelper';
import { useUsersMyTeams } from 'hooks/useUserTeams';
import ShowHistoryChanges from 'components/HistoryChanges';

function ModalSchedulingProtocol({
  show,
  onModalToggle,
  setSelectedSchedule,
  schedulingId,
  setschedulingId,
  isToCreateScheduling,
  isToUpdateScheduling,
  protocolId,
  getProtocolById,
  isFromNotification
}) {
  const [schedulingData, setSchedulingData] = useState(null);
  const [descriptionLength, setDescriptionLength] = useState(0);
  const { control, errors, setValue, handleSubmit, reset } = useForm();
  const [selectedType, setSelectedType] = useState('');
  const [userLogin, setUserLogin] = useState(null);
  const [scheduleChangesHistory, setScheduleChangesHistory] = useState([]);

  const { usersInMyTeamsFullInfo } = useUsersMyTeams();

  useEffect(() => {
    if (show) {
      const login = localStorage.getItem('login');
      if (login) {
        setUserLogin(login);
      }

      if (isToUpdateScheduling && schedulingId) {
        ProtocolSchedulingService.getProtocolScheduleById(schedulingId)
          .then((response) => {
            const schedulingDataDto = response.data.content;
            const [date, fullHour] = schedulingDataDto.schedule_date.split(' ');
            schedulingDataDto.scheduling_date = date;
            schedulingDataDto.scheduling_hour = fullHour;
            schedulingDataDto.destination = maskPhoneNumber(
              schedulingDataDto.destination
            );

            reset(schedulingDataDto);
            setSchedulingData(schedulingDataDto);
            setDescriptionLength(schedulingDataDto.description?.length || 0);
            setSelectedType(schedulingDataDto.type);
          })
          .catch((error) => console.error(error));

        ProtocolSchedulingService.getScheduleHistoryById(schedulingId)
          .then((response) => {
            const historyData = response.data.content;
            historyData.forEach((item) => {
              item.createdAt = item.created_at;
              item.changeDescription = item.change_description;
            });
            setScheduleChangesHistory(historyData);
          })
          .catch((error) => console.error(error));
      } else {
        reset({});
        setSchedulingData(null);
        setDescriptionLength(0);
        setSelectedType('');
        setScheduleChangesHistory([]);
      }
    } else {
      reset({});
      setSchedulingData(null);
      setDescriptionLength(0);
      setSelectedType('');
      setScheduleChangesHistory([]);
      if (isToUpdateScheduling) {
        setschedulingId(null);
      }
    }
  }, [show, isToUpdateScheduling, schedulingId, reset, setschedulingId]);

  const cancelAction = () => {
    isToUpdateScheduling && setschedulingId(null);
    onModalToggle(!show);
  };

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  const onSubmit = (data) => {
    const today = new Date();
    if (isToCreateScheduling) {
      data.protocol_id = protocolId;
    }

    if (isToUpdateScheduling) {
      data.protocol_id = schedulingData?.protocol_id;
    }

    data.responsible = userLogin;

    const [day, month, year] = data.scheduling_date.toString().split('/');
    const [hour, minute, seconds] = data.scheduling_hour.toString().split(':');
    const schedule_date = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(seconds) || 0
    );

    data.schedule_date = schedule_date;

    if (data.schedule_date < today) {
      toast.error(
        'Horário inválido, defina uma data ou hora maior que a atual',
        {
          autoClose: 3000,
          closeOnClick: 'true'
        }
      );
    } else {
      delete data.scheduling_date;
      delete data.scheduling_hour;

      // Ajusta o horário para o fuso brasileiro
      data.schedule_date = DatesHelper.applyHoursToISO(
        schedule_date.toISOString(),
        -3
      );

      if (data?.destination?.length === 0) {
        delete data.destination;
      } else {
        data.destination = removeMaskSpecialCharacters(data.destination);
      }
      if (isToCreateScheduling) {
        ProtocolSchedulingService.createProtocolSchedule(data)
          .then(() => {
            toast.success('Agendamento realizado com sucesso!', {
              autoClose: 3000,
              closeOnClick: true
            });
            getProtocolById();
          })
          .catch((error) => {
            toast.error(
              error || 'Erro ao criar um novo agendamento de ligação',
              {
                autoClose: 3000,
                closeOnClick: true
              }
            );
          });
      } else if (isToUpdateScheduling) {
        ProtocolSchedulingService.rescheduleProtocolScheduling(
          schedulingId,
          data
        )
          .then(() => {
            toast.success('Agendamento reagendado com sucesso!', {
              autoClose: 3000,
              closeOnClick: true
            });
            !isFromNotification && getProtocolById();
            !isFromNotification && getProtocolById();
          })
          .catch((error) => {
            toast.error(error || 'Erro ao reagendar a ligação', {
              autoClose: 3000,
              closeOnClick: true
            });
          });
      } else {
        setSelectedSchedule(data);
      }
      onModalToggle(!show);
      isToUpdateScheduling && setschedulingId(null);
    }
  };

  const toISOFormat = useCallback(async (dateTimeString, bar = false) => {
    const [date] = dateTimeString.split(' ');
    const [DD, MM, YYYY] = date.split('/');
    if (bar) return `${YYYY}/${MM}/${DD}`;
    return `${YYYY}-${MM}-${DD}`;
  }, []);

  return (
    <Modal
      className="modal-dialog-centered"
      isOpen={show}
      returnFocusAfterClose={false}
      unmountOnClose={true}
      size="lg"
    >
      <div className="modal-header">
        <h6 className="modal-title" id="modal-title-default">
          {schedulingId
            ? 'Reagendar ligação'
            : 'Adicionar agendamento ao protocolo'}
        </h6>
        <button
          aria-label="Close"
          className="close"
          data-dismiss="modal"
          type="button"
          onClick={() => {
            onModalToggle(!show);
            isToUpdateScheduling && setschedulingId(null);
          }}
        >
          <span aria-hidden={true}>&times;</span>
        </button>
      </div>
      <ModalBody>
        <Form
          className="needs-validation"
          onSubmit={(event) => {
            event.stopPropagation();
            handleSubmit(onSubmit)(event);
          }}
          id="modalForm"
        >
          <Row>
            <Col className="mb-3" sm="6">
              <label className="form-control-label">Data</label>
              <Controller
                render={(props) => (
                  <DatePicker
                    fieldName="scheduling_date"
                    valueController={setValue}
                    defaultValue={props.value}
                  />
                )}
                control={control}
                defaultValue=""
                rules={{
                  required: 'Campo obrigatório',
                  validate: async (value) => {
                    const today = new Date().toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric'
                    });
                    if (
                      new Date(await toISOFormat(value, true)) <
                      new Date(await toISOFormat(today, true))
                    ) {
                      return 'A data não pode ser inferior a de hoje!';
                    } else {
                      return true;
                    }
                  }
                }}
                name="scheduling_date"
              />
              <ErrorMessage
                errors={errors}
                name="scheduling_date"
                render={({ message }) => errorFormMessage(message)}
              />
            </Col>
            <Col className="mb-3" sm="6">
              <label className="form-control-label">Horário</label>
              <Controller
                render={(props) => (
                  <Input
                    value={props.value}
                    onChange={props.onChange}
                    type="time"
                    step="2"
                    placeholder="scheduling_hour"
                  />
                )}
                control={control}
                defaultValue=""
                rules={{
                  required: 'Campo obrigatório'
                }}
                name="scheduling_hour"
              />
              <ErrorMessage
                errors={errors}
                name="scheduling_hour"
                render={({ message }) => errorFormMessage(message)}
              />
            </Col>
          </Row>
          <Row>
            <Col className="mb-3" sm="6">
              <label className="form-control-label">Tipo</label>
              <Controller
                render={(props) => (
                  <FlexSelect
                    dataOptions={typeOptions || []}
                    isMulti={false}
                    isClearable={true}
                    value={props.value}
                    valueController={setValue}
                    closeMenuOnSelect={true}
                    fieldName="type"
                    labelName="label"
                    valueName="value"
                    onChange={(selectedOption) => {
                      setSelectedType(selectedOption);
                    }}
                  />
                )}
                control={control}
                defaultValue=""
                rules={{
                  required: 'Campo obrigatório'
                }}
                name="type"
              />
              <ErrorMessage
                errors={errors}
                name="type"
                render={({ message }) => errorFormMessage(message)}
              />
            </Col>
            <Col className="mb-3" sm="6">
              <label className="form-control-label">
                Agente a ser notificado
              </label>
              <Controller
                render={(props) => (
                  <FlexSelect
                    dataOptions={usersInMyTeamsFullInfo || []}
                    isMulti={false}
                    isClearable={true}
                    value={props.value}
                    valueController={setValue}
                    closeMenuOnSelect={true}
                    fieldName="designated_agent_login"
                    labelName="login"
                    valueName="login"
                  />
                )}
                control={control}
                name="designated_agent_login"
                defaultValue=""
              />
              <ErrorMessage
                errors={errors}
                name="designated_agent_login"
                render={({ message }) => errorFormMessage(message)}
              />
            </Col>
          </Row>
          <Row>
            <Col className="mb-3" sm="6">
              <Row className="mb-5">
                <Col md="12">
                  <LabelInput value="Descrição" />
                  <Controller
                    render={(props) => {
                      const handleInputChange = (e) => {
                        setDescriptionLength(e.target.value.length);
                        props.onChange(e);
                      };

                      return (
                        <>
                          <Input
                            type="textarea"
                            rows="3"
                            name="description"
                            value={props.value}
                            onChange={handleInputChange}
                            placeholder="Descrição"
                            maxLength="255"
                          />
                          <div
                            style={{
                              color: descriptionLength === 255 ? 'red' : 'black'
                            }}
                          >
                            {descriptionLength}/255 caracteres
                          </div>
                        </>
                      );
                    }}
                    control={control}
                    name="description"
                    defaultValue=""
                    rules={{
                      required: 'Campo obrigatório',
                      maxLength: {
                        value: 255,
                        message:
                          'A descrição não pode ter mais de 255 caracteres'
                      }
                    }}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="description"
                    render={({ message }) => errorFormMessage(message)}
                  />
                </Col>
              </Row>
            </Col>
            {selectedType === 'TELEFONIA' && (
              <Col className="mb-3" sm="6">
                <label className="form-control-label">Destino</label>
                <Controller
                  render={(props) => (
                    <InputGroup>
                      <TelephoneMask
                        valueController={setValue}
                        fieldName="destination"
                        value={props.value}
                        placeholder="(DDD) 0000-0000"
                      />
                    </InputGroup>
                  )}
                  control={control}
                  defaultValue=""
                  name="destination"
                  rules={{
                    required: 'Campo obrigatório'
                  }}
                />
                <ErrorMessage
                  errors={errors}
                  name="destination"
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
            )}
          </Row>

          <Row className="mb-5 mt-4">
            <Col md="12">
              <ShowHistoryChanges data={scheduleChangesHistory} />
            </Col>
          </Row>

          <ReactButton btnColor="confirmation" type="submit" form="modalForm">
            Salvar
          </ReactButton>

          <ReactButton
            btnColor="cancelation"
            onClick={() => {
              return cancelAction();
            }}
          >
            Cancelar
          </ReactButton>
        </Form>
      </ModalBody>
    </Modal>
  );
}

export default ModalSchedulingProtocol;
